import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "constants/routes";
import { useDictionary } from "hooks/use-dictionary";
import { useAuth } from "providers/auth";
import { REDIRECT_URL } from "constants/api";
import Layout from "components/layout";
import Link from "components/link";

type SingleSignOnData = {
  ssoSession: string;
  specialistPersonalCode: string;
  licenseTypeClassifierId: string;
  specialistRequestDate: string;
};

/**
 * The SSO session token is single-use. When the user state changes, pages.tsx rebuilds the
 * router and this page mounts again; sharing one in-flight exchange per token keeps the second
 * mount from spending the token again (which fails) and lets it navigate with the live router.
 */
const ssoExchanges = new Map<string, Promise<boolean>>();

export const resetSsoExchangesForTests = () => ssoExchanges.clear();

const exchangeOnce = (ssoSession: string, exchange: (ssoSession: string) => Promise<{ success: boolean }>) => {
  let pending = ssoExchanges.get(ssoSession);

  if (!pending) {
    pending = exchange(ssoSession)
      .then(({ success }) => success)
      .catch(() => false);
    ssoExchanges.set(ssoSession, pending);
  }

  return pending;
};

const SingleSignOnPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dict = useDictionary();
  const { loginSingleSignOn } = useAuth();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const ssoSession = searchParams.get("ssoSession");
    const licenseTypeClassifierId = searchParams.get("licenseTypeClassifierId");
    const specialistPersonalCode = searchParams.get("specialistPersonalCode");
    const specialistRequestDate = searchParams.get("specialistRequestDate");

    if (!ssoSession || !licenseTypeClassifierId || !specialistPersonalCode || !specialistRequestDate) {
      setFailed(true);

      return;
    }

    const data: SingleSignOnData = { ssoSession, specialistPersonalCode, licenseTypeClassifierId, specialistRequestDate };
    let isActive = true;

    localStorage.removeItem(REDIRECT_URL);

    // No logout first: the exchange response replaces the session cookies, and a logout racing
    // the exchange could revoke the session it has just issued.
    exchangeOnce(data.ssoSession, loginSingleSignOn).then((success) => {
      if (!isActive) {
        return;
      }

      if (!success) {
        setFailed(true);

        return;
      }

      navigate(ROUTES.user + ROUTES.controllingAuthority.specialistSearch, {
        state: {
          specialistCode: data.specialistPersonalCode,
          licenseTypeClassifierId: data.licenseTypeClassifierId,
          specialistRequestDate: data.specialistRequestDate,
        },
      });
    });

    return () => {
      isActive = false;
    };
  }, [location.search]);

  return (
    <Layout>
      <div className="flex flex-col gap-4 justify-center items-center h-screen text-2xl">
        {failed ? (
          <>
            <div>{dict.messages.error}</div>
            <Link to={ROUTES.login.home} className="text-base">
              {dict.login.otp.backToLogin}
            </Link>
          </>
        ) : (
          dict.common.loading
        )}
      </div>
    </Layout>
  );
};

export default SingleSignOnPage;
